import { motion } from "framer-motion";

/**
 * Animated brand logo:
 *   - sleek camera icon stays static at centre
 *   - "MARKETING", "MEDIA", "MESSAGE", "MONEY" rotate clockwise around it
 *   - each word stays upright relative to viewport during the orbit
 */
export default function AnimatedLogo({
    size = 320,
    ring = 100,
    wordColor = "#0F3D7A",
    accent = "#0F3D7A",
    speed = 18,
}) {
    const words = ["MARKETING", "MEDIA", "MESSAGE", "MONEY"];
    const ringSm = Math.max(ring * 0.85, 90);
    const ringMd = Math.max(ring * 0.85, 80);
    const ringLg = ring;
    const sizeSm = Math.max(size * 0.75, 240);
    const sizeMd = Math.max(size * 0.9, 300);
    const sizeLg = size;

    const currentRing = ringSm;
    const currentSize = sizeSm;

    return (
        <div
            data-testid="animated-logo"
            className="relative inline-flex items-center justify-center w-[80vw] h-[80vw] max-w-[320px] max-h-[320px] sm:w-[70vw] sm:h-[70vw] sm:max-w-[360px] sm:max-h-[360px] lg:w-[360px] lg:h-[360px]"
            aria-label="Commmmuniti animated logo"
        >
            <span
                aria-hidden
                className="absolute rounded-full border border-dashed"
                style={{
                    width: currentRing * 2,
                    height: currentRing * 2,
                    borderColor: `${accent}22`,
                }}
            />

            {/* Static camera icon (centered & sleek) */}
            <svg
                viewBox="0 0 100 100"
                className="relative z-10 w-[35%] h-[35%] sm:w-[42%] sm:h-[42%]"
                aria-hidden
            >
                <rect x="14" y="30" width="72" height="48" rx="9" fill={accent} />
                <path d="M38 30 L42 22 H58 L62 30 Z" fill={accent} />
                <rect x="22" y="38" width="6" height="3" rx="1.5" fill="#FFFFFF" opacity=".65" />
                <circle cx="50" cy="54" r="16" fill="#FFFFFF" opacity=".08" />
                <circle cx="50" cy="54" r="14" fill="none" stroke="#FFFFFF" strokeWidth="2" />
                <circle cx="50" cy="54" r="9" fill="#FFFFFF" opacity=".18" />
                <circle cx="50" cy="54" r="9" fill="none" stroke="#FFFFFF" strokeWidth="1.2" />
                <circle cx="46" cy="50" r="2" fill="#FFFFFF" opacity=".8" />
            </svg>

            {/* Rotating words wrapper — rotates the orbit clockwise */}
            <motion.div
                className="absolute inset-0"
                animate={{ rotate: 360 }}
                transition={{ duration: speed, repeat: Infinity, ease: "linear" }}
            >
                {words.map((word, i) => {
                    const angle = (i * 360) / words.length;
                    return (
                        <div
                            key={word}
                            className="absolute left-1/2 top-1/2"
                            style={{
                                transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-${currentRing}px) rotate(${-angle}deg)`,
                            }}
                        >
                            {/* counter-rotate continuously to cancel the parent rotation */}
                            <motion.span
                                className="block font-display font-bold tracking-[0.18em] text-[10px] sm:text-[12px] md:text-[14px] uppercase whitespace-nowrap"
                                animate={{ rotate: -360 }}
                                transition={{
                                    duration: speed,
                                    repeat: Infinity,
                                    ease: "linear",
                                }}
                                style={{
                                    color: wordColor,
                                    transformOrigin: "50% 50%",
                                }}
                            >
                                {word}
                            </motion.span>
                        </div>
                    );
                })}
            </motion.div>
        </div>
    );
}
